import * as yup from "yup";

import {
  EarlyRepaymentValidation,
  FREE_EARLY_REPAYMENT,
} from "@krea/shared/features/early-repayment";
import type { Currency } from "@krea/shared/types/common";

import {
  BidTypeEnum,
  PaymentFrequency,
  RealEstateAmortizationMethodEnum,
} from "@/features/common/enums";
import type { IRealEstateBidInput } from "@/features/lender-responses/types/lender-response";

import { AdministrationFeesValidation } from "../utils/validators/AdministrationFeesValidation";
import { AmountValidation } from "../utils/validators/AmountValidation";
import { LenderMonthlyAmountValidation } from "../utils/validators/LenderMonthlyAmountValidation";
import { LenderMonthlyInterestValidation } from "../utils/validators/LenderMonthlyInterestValidation";
import { PaybackTimeMonthsValidation } from "../utils/validators/PaybackTimeMonthsValidation";
import { PaymentFrequencyValidation } from "../utils/validators/PaymentFrequencyValidation";
import { SetupFeeValidation } from "../utils/validators/SetupFeeValidation";
import { TotalCostValidation } from "../utils/validators/TotalCostValidation";

export const realEstateBidSchema: (
  currency: Currency,
  formatWithCurrency: (value: number) => string,
) => yup.ObjectSchema<IRealEstateBidInput> = (currency, formatWithCurrency) =>
  yup.object().shape({
    type: yup
      .string()
      .required("Type is required")
      .oneOf([BidTypeEnum.REAL_ESTATE])
      .default(BidTypeEnum.REAL_ESTATE),
    amount: AmountValidation(currency, formatWithCurrency),
    paybackTimeMonths: PaybackTimeMonthsValidation(),
    amortizationMethod: yup
      .string()
      .oneOf(Object.values(RealEstateAmortizationMethodEnum))
      .required("Amortisation method is required"),
    paymentFrequency: PaymentFrequencyValidation(),
    lenderMonthlyInterest: LenderMonthlyInterestValidation({
      isFunction: () => true,
    }),
    lenderMonthlyAmount: LenderMonthlyAmountValidation(
      currency,
      formatWithCurrency,
    ),
    totalCost: TotalCostValidation(formatWithCurrency),
    ...EarlyRepaymentValidation(),
    administrationFees: AdministrationFeesValidation(formatWithCurrency),
    setupFee: SetupFeeValidation(formatWithCurrency),
    amortizationInPercent: yup.number().when("amortizationMethod", {
      is: (amortizationMethod: RealEstateAmortizationMethodEnum) =>
        amortizationMethod === RealEstateAmortizationMethodEnum.LINEAR,
      then: (schema) =>
        schema
          .required("Amortisation in % is required")
          .typeError("Amortisation in % is required")
          .min(
            0,
            (schema) =>
              `Amortisation in % needs to be greater than ${schema.min}`,
          )
          .max(
            100,
            (schema) =>
              `Amortisation in % can't be greater than ${schema.max}%`,
          ),
      otherwise: (schema) => schema.nullable(),
    }),
  });

export const realEstateBidDefaultValues: IRealEstateBidInput = {
  type: BidTypeEnum.REAL_ESTATE,
  amount: undefined,
  paybackTimeMonths: undefined,
  amortizationMethod: RealEstateAmortizationMethodEnum.LINEAR,
  paymentFrequency: PaymentFrequency.MONTHLY,
  lenderMonthlyInterest: undefined,
  lenderMonthlyAmount: undefined,
  totalCost: undefined,
  earlyRepayment: FREE_EARLY_REPAYMENT,
  administrationFees: undefined,
  setupFee: undefined,
  amortizationInPercent: undefined,
};
